import { Fragment, useState } from "react";
import clsx from "clsx";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

import LoadingWheel from "../shared/LoadingWheel";
import { useImportSyncRun } from "../../data/ImportSync/useImportSync";
import { IImportSyncFailure, IImportSyncRun } from "../../models/ImportSync/ImportSync";
import {
  displayState,
  formatDateTime,
  formatDuration,
  formatSourceTime,
  numberFormat,
  runDurationMs,
  stateMeta,
  SyncDisplayState,
  SyncStatePill,
  useNow,
} from "./syncMeta";

// -----------------------------------------------------------------------------
// Strip — one block per run, oldest on the left, coloured by how it ended.
//
// Deliberately not a bar chart of vehicle counts: a first full sync is ~180k vehicles and a
// nightly one a few hundred, so on a shared axis every nightly bar would be invisible. What the
// strip is for is spotting the pattern — nights that failed, a run of stalls — and the table
// below carries the numbers.
// -----------------------------------------------------------------------------

export function RunHistoryStrip({ runs }: { runs: IImportSyncRun[] }) {
  const [hovered, setHovered] = useState<{ run: IImportSyncRun; left: number } | null>(null);
  const now = useNow(30_000);
  const ordered = [...runs].reverse();

  // Anchored under the hovered block, held inside the strip so it never runs off the right edge.
  const show = (run: IImportSyncRun, target: HTMLElement) => {
    const stripWidth = target.parentElement?.clientWidth ?? 0;
    const tooltipWidth = 288;
    const left = Math.max(0, Math.min(target.offsetLeft - tooltipWidth / 2, stripWidth - tooltipWidth));
    setHovered({ run, left });
  };

  const statesShown = Array.from(new Set(ordered.map(displayState)));

  return (
    <div className="space-y-3">
      <div className="relative">
        <div className="flex h-10 items-end gap-0.5" role="list" aria-label="Recent sync runs, oldest first">
          {ordered.map((run) => {
            const state = displayState(run);

            return (
              <button
                key={run.id}
                type="button"
                role="listitem"
                aria-label={`${stateMeta[state].label}, started ${formatDateTime(run.startedOn)}`}
                className="group relative flex h-full min-w-[10px] max-w-[28px] flex-1 items-end focus:outline-none"
                onMouseEnter={(e) => show(run, e.currentTarget)}
                onMouseLeave={() => setHovered(null)}
                onFocus={(e) => show(run, e.currentTarget)}
                onBlur={() => setHovered(null)}
              >
                <span
                  className={clsx(
                    "block h-8 w-full rounded-[4px] transition-all group-hover:h-10 group-focus-visible:h-10 group-focus-visible:ring-2 group-focus-visible:ring-offset-1 group-focus-visible:ring-gray-400",
                    state === "Running" && "animate-pulse",
                  )}
                  style={{ backgroundColor: stateMeta[state].swatch }}
                />
              </button>
            );
          })}
        </div>

        {hovered && (
          <div
            className="pointer-events-none absolute top-12 z-10 w-72 rounded-md border border-gray-200 bg-white p-3 text-xs shadow-lg"
            style={{ left: hovered.left }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-gray-900">{formatDateTime(hovered.run.startedOn)}</span>
              <SyncStatePill state={displayState(hovered.run)} />
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-gray-600">
              <dt>Covers</dt>
              <dd className="text-right text-gray-900">{describeMode(hovered.run)}</dd>
              <dt>Vehicles</dt>
              <dd className="text-right tabular-nums text-gray-900">
                {numberFormat.format(hovered.run.processedVehicles)} / {numberFormat.format(hovered.run.totalVehicles)}
              </dd>
              <dt>Failed</dt>
              <dd className="text-right tabular-nums text-gray-900">{numberFormat.format(hovered.run.failedVehicles)}</dd>
              <dt>Took</dt>
              <dd className="text-right tabular-nums text-gray-900">{formatDuration(runDurationMs(hovered.run, now))}</dd>
            </dl>
          </div>
        )}
      </div>

      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600" aria-label="Legend">
        {statesShown.map((state) => (
          <LegendItem key={state} state={state} />
        ))}
      </ul>
    </div>
  );
}

function LegendItem({ state }: { state: SyncDisplayState }) {
  const meta = stateMeta[state];
  const Icon = meta.icon;

  return (
    <li className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: meta.swatch }} aria-hidden />
      <Icon className="h-3.5 w-3.5 text-gray-500" aria-hidden />
      {meta.label}
    </li>
  );
}

// -----------------------------------------------------------------------------
// Table
// -----------------------------------------------------------------------------

export function RunHistoryTable({ customerId, runs }: { customerId: string; runs: IImportSyncRun[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const now = useNow(30_000);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead>
          <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <th className="w-6 py-2" />
            <th className="py-2 pr-4">Started</th>
            <th className="py-2 pr-4">Status</th>
            <th className="py-2 pr-4">Covers</th>
            <th className="py-2 pr-4 text-right">Vehicles</th>
            <th className="py-2 pr-4 text-right">Failed</th>
            <th className="py-2 pr-4 text-right">Images</th>
            <th className="py-2 pr-4 text-right">Took</th>
            <th className="py-2 pr-4">Machine</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {runs.map((run) => {
            const isOpen = expanded === run.id;
            const canExpand = run.failedVehicles > 0 || !!run.error;
            const Chevron = isOpen ? ChevronDownIcon : ChevronRightIcon;

            return (
              <Fragment key={run.id}>
                <tr
                  className={clsx(canExpand && "cursor-pointer hover:bg-gray-50", isOpen && "bg-gray-50")}
                  onClick={() => canExpand && setExpanded(isOpen ? null : run.id)}
                >
                  <td className="py-2 text-gray-400">{canExpand && <Chevron className="h-4 w-4" aria-hidden />}</td>
                  <td className="whitespace-nowrap py-2 pr-4 text-gray-900">{formatDateTime(run.startedOn)}</td>
                  <td className="py-2 pr-4">
                    <SyncStatePill state={displayState(run)} />
                  </td>
                  <td className="whitespace-nowrap py-2 pr-4 text-gray-600">
                    {describeMode(run)}
                    {run.resumedFromRunId && <span className="ml-1 text-xs text-gray-400">(resumed)</span>}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-4 text-right tabular-nums text-gray-900">
                    {numberFormat.format(run.processedVehicles)}
                    <span className="text-gray-400"> / {numberFormat.format(run.totalVehicles)}</span>
                  </td>
                  <td
                    className={clsx(
                      "py-2 pr-4 text-right tabular-nums",
                      run.failedVehicles > 0 ? "font-medium text-amber-700" : "text-gray-400",
                    )}
                  >
                    {numberFormat.format(run.failedVehicles)}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums text-gray-600">{numberFormat.format(run.imagesUploaded)}</td>
                  <td className="whitespace-nowrap py-2 pr-4 text-right tabular-nums text-gray-600">
                    {formatDuration(runDurationMs(run, now))}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-4 text-gray-500">{run.machineName ?? "—"}</td>
                </tr>

                {isOpen && (
                  <tr className="bg-gray-50">
                    <td />
                    <td colSpan={8} className="pb-4 pr-4">
                      <RunDetail customerId={customerId} run={run} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RunDetail({ customerId, run }: { customerId: string; run: IImportSyncRun }) {
  const detail = useImportSyncRun(customerId, run.id);

  return (
    <div className="space-y-3">
      {run.error && (
        <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{run.error}</p>
      )}
      {run.failedVehicles > 0 &&
        (detail.isLoading ? (
          <div className="min-h-[80px]">
            <LoadingWheel />
          </div>
        ) : detail.isError ? (
          <p className="text-sm text-red-700">{detail.error.message}</p>
        ) : (
          <FailureList failures={detail.data?.failures ?? []} total={run.failedVehicles} />
        ))}
    </div>
  );
}

// -----------------------------------------------------------------------------

export function FailureList({ failures, total }: { failures: IImportSyncFailure[]; total: number }) {
  if (failures.length === 0) return <p className="text-sm text-gray-500">No failures.</p>;

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        {failures.length < total
          ? `Latest ${numberFormat.format(failures.length)} of ${numberFormat.format(total)} failed vehicles.`
          : `${numberFormat.format(total)} failed vehicle${total === 1 ? "" : "s"}.`}{" "}
        Each is tried again on the next run.
      </p>
      <div className="max-h-80 overflow-y-auto rounded border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-100 text-xs">
          <thead className="sticky top-0 bg-white">
            <tr className="text-left font-medium text-gray-500">
              <th className="px-3 py-1.5">Vehicle</th>
              <th className="px-3 py-1.5">Reason</th>
              <th className="px-3 py-1.5 text-right">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {failures.map((failure, i) => (
              <tr key={`${failure.sourceId}-${i}`}>
                <td className="whitespace-nowrap px-3 py-1.5 font-medium text-gray-900">
                  {failure.reference ?? `#${failure.sourceId}`}
                </td>
                <td className="px-3 py-1.5 text-gray-700 break-words">{failure.reason ?? "—"}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right text-gray-500">{formatDateTime(failure.at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function describeMode(run: IImportSyncRun) {
  return run.mode === "Full" ? "Everything" : `Changes since ${formatSourceTime(run.changesSince)}`;
}
